import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import { getTenantFromSubdomain } from '../utils/tenant';
import AddTenantUserModal from '../components/AddTenantUserModal';
import BulkUploadModal from '../components/BulkUploadModal';
import './UniversityUserManagement.css';

const LIMIT = 15;

const ROLES = [
  { value: '', label: 'All Roles' },
  { value: 'college_admin', label: 'College Admin' },
  { value: 'coe', label: 'COE' },
  { value: 'hod', label: 'HOD' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'subject_matter_expert', label: 'Subject Matter Expert' },
  { value: 'examiner', label: 'Examiner' },
];

const STATUSES = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
];

const STATUS_BADGE = {
  active: 'uum-badge--success',
  inactive: 'uum-badge--warning',
  pending: 'uum-badge--info',
  suspended: 'uum-badge--danger',
};

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="7" r="5" />
    <path d="M11 11l3.5 3.5" />
  </svg>
);

const UniversityUserManagement = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers]           = useState([]);
  const [colleges, setColleges]     = useState([]);
  const [filters, setFilters]       = useState({ college_id: '', role: '', status: '', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const tenantSlug = getTenantFromSubdomain();

  const fetchColleges = useCallback(async () => {
    try {
      const { data } = await API.get(`/public/colleges?university_id=${user.university_id}`);
      setColleges(data.colleges || []);
    } catch {}
  }, [user.university_id]);

  const fetchUsers = useCallback(async (p, f) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (f.college_id) params.set('college_id', f.college_id);
      if (f.role) params.set('role', f.role);
      if (f.status) params.set('status', f.status);
      if (f.search) params.set('search', f.search);
      const { data } = await API.get(`/auth/university-users?${params}`);
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleUserStatus = async (userId, currentStatus) => {
    setTogglingId(userId);
    try {
      const is_active = currentStatus !== 'active';
      const { data } = await API.put(`/auth/users/${userId}/status`, { is_active });
      if (data.success) {
        setUsers(prev => prev.map(u =>
          u.user_id === userId ? { ...u, status: is_active ? 'active' : 'inactive' } : u
        ));
      }
    } catch (err) {
      console.error('Toggle status error:', err);
    } finally {
      setTogglingId(null);
    }
  };

  useEffect(() => { fetchColleges(); }, [fetchColleges]);
  useEffect(() => { fetchUsers(page, filters); }, [page, filters, fetchUsers]);

  const handleFilterChange = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleFilterChange('search', searchInput);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line

  const roleLabel = (role) => {
    if (role === 'coe') return 'COE';
    if (role === 'hod') return 'HOD';
    if (role === 'subject_matter_expert') return 'SME';
    return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="uum">
      <div className="uum__header">
        <div>
          <h1 className="uum__title">User Management</h1>
          <p className="uum__subtitle">Manage all users registered in your university</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span className="uum__count">{total} user{total !== 1 ? 's' : ''} found</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowBulk(true)} disabled={!tenantSlug}>↑ Bulk Upload</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddUser(true)} disabled={!tenantSlug}>+ Add User</button>
        </div>
      </div>

      {showAddUser && tenantSlug && (
        <AddTenantUserModal
          tenantSlug={tenantSlug}
          tenantType="university"
          isCollegeAdmin={false}
          onClose={() => setShowAddUser(false)}
          onSuccess={() => { setShowAddUser(false); fetchUsers(1, filters); setPage(1); }}
        />
      )}
      {showBulk && tenantSlug && (
        <BulkUploadModal
          tenantSlug={tenantSlug}
          onClose={() => setShowBulk(false)}
          onSuccess={() => { setShowBulk(false); fetchUsers(1, filters); setPage(1); }}
        />
      )}

      {error && <div className="form-error">{error}</div>}

      <div className="uum__card">
        <div className="uum__filters">
          <div className="uum__search-wrap">
            <SearchIcon />
            <input
              type="text"
              className="uum__input"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>

          <div className="uum__select-wrap">
            <select
              className="uum__select"
              value={filters.college_id}
              onChange={e => handleFilterChange('college_id', e.target.value)}
            >
              <option value="">All Colleges</option>
              {colleges.map(c => (
                <option key={c.college_id} value={c.college_id}>{c.college_name}</option>
              ))}
            </select>
            <ChevronDown />
          </div>

          <div className="uum__select-wrap">
            <select
              className="uum__select"
              value={filters.role}
              onChange={e => handleFilterChange('role', e.target.value)}
            >
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <ChevronDown />
          </div>

          <div className="uum__select-wrap">
            <select
              className="uum__select"
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
            >
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <ChevronDown />
          </div>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <>
            <div className="table-container">
              {users.length === 0 ? (
                <div className="empty-state">
                  <p>No users found matching the selected filters.</p>
                </div>
              ) : (
                <table className="uum__table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>College</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id}>
                        <td data-label="Name">
                          <div className="uum__cell-name">{u.name}</div>
                          {u.designation && (
                            <div className="uum__cell-designation">{u.designation}</div>
                          )}
                        </td>
                        <td data-label="Email">
                          <a href={`mailto:${u.email}`} className="uum__email">{u.email}</a>
                        </td>
                        <td data-label="Role">
                          <div className="uum__badge-group">
                            <span className="uum__badge uum__badge--role">
                              {roleLabel(u.role)}
                            </span>
                            {u.university_certified === 1 && (
                              <span className="uum__badge uum__badge--pool" title="University Pool Access">
                                Pool
                              </span>
                            )}
                          </div>
                        </td>
                        <td data-label="College" className="uum__cell-muted">{u.college_name || '\u2014'}</td>
                        <td data-label="Department" className="uum__cell-muted">{u.department_name || '\u2014'}</td>
                        <td data-label="Status">
                          <span className={`uum__badge ${STATUS_BADGE[u.status] || 'uum-badge--info'}`}>
                            {u.status}
                          </span>
                        </td>
                        <td data-label="Joined" className="uum__cell-date">
                          {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td data-label="Actions">
                          {u.user_id !== user.user_id && (u.status === 'active' || u.status === 'inactive') && (
                            <button
                              className={`uum__action-btn ${u.status === 'active' ? 'uum__action-btn--deactivate' : 'uum__action-btn--activate'}`}
                              disabled={togglingId === u.user_id}
                              onClick={() => toggleUserStatus(u.user_id, u.status)}
                            >
                              {togglingId === u.user_id ? (
                                <span className="uum__toggle-spinner" />
                              ) : u.status === 'active' ? (
                                'Deactivate'
                              ) : (
                                'Activate'
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="uum__pagination">
                <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <span className="uum__page-info">
                  Page {page} of {totalPages}
                </span>
                <button className="btn btn-secondary btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UniversityUserManagement;
